import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';

import { StudentRosterPolicy } from 'src/app/domain/student/services/student-roster-policy.service';
import { AccountState } from 'src/app/store/account/account.state';

/**
 * Student Creation Guard
 *
 * Blocks the notebook "add a student" route when the active partner has
 * turned `allowStudentCreation` off. Applies to administrators too —
 * creation moves to the Administration portal, which this guard never gates.
 *
 * The UI already hides the affordance; this covers the ways a user reaches the
 * page without it — a bookmark, a shared link, browser history, or a hand-typed
 * URL. Denied users go back to their caseload rather than an error page, since
 * being unable to create a student is a normal district configuration, not a
 * failure.
 *
 * Compose AFTER `authGuard`, `profileGuard`, and `partnerMemberGuard` so the
 * active profile and partner are resolved by the time this runs.
 *
 * Note this is a UX gate, not a security boundary — see
 * {@link StudentRosterPolicy} for why, and what a real boundary would cost.
 *
 * @example
 * {
 *   path: 'caseload/students/add',
 *   canActivate: [studentCreationGuard],
 *   loadComponent: () => import('./students/students-add/students-add.page').then(m => m.NotebookStudentsAddPage),
 * }
 */
export const studentCreationGuard: CanActivateFn = () => {
  const policy = inject(StudentRosterPolicy);
  const accountState = inject(AccountState);
  const router = inject(Router);

  if (policy.canCreateStudent()) return true;

  void router.navigateByUrl(accountState.notebookUrl('caseload'));
  return false;
};
